// KnockHistorySection — May 6, 2026
// Chronological feed of canvas activity on this deal's knock_pin_id.
// Renders the original pin-drop event + every canvas_visits row + any
// pending_canvas_appointments row. Each row shows actor, outcome, GPS
// distance from the pin, voice/photo media, and notes.
//
// Skips the section silently if the deal has no knock_pin_id (SR-intake
// deals or pre-canvas-tool deals).

import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import VisitMediaInline from "../canvas/VisitMediaInline";

interface Entry {
  id: string;
  kind: "pin_dropped" | "visit" | "pending_appt";
  at: string;
  actor_name: string | null;
  outcome: string | null;
  notes: string | null;
  voice_note_url: string | null;
  photo_storage_path: string | null;
  appointment_at: string | null;
}

const OUTCOME_LABEL: Record<string, string> = {
  no_answer:    "🚪 No answer",
  contact_made: "👋 Contact made",
  follow_up:    "🔁 Follow up",
  rent:         "🏠 Rent",
  appt_set:     "✅ Appt set",
  wrong_address: "✕ Wrong address",
  do_not_knock: "🚫 Do not knock",
};

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

export default function KnockHistorySection({ knockPinId }: { knockPinId: string | null }) {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!knockPinId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);

      const [pinRes, visitsRes, pendingRes] = await Promise.all([
        // Pin-drop event
        supabase
          .from("knock_pins")
          .select("id, dropped_at, dropped_by, notes, dropper:team_roster!knock_pins_dropped_by_fkey(full_name)")
          .eq("id", knockPinId)
          .maybeSingle(),
        // All visits on this pin
        supabase
          .from("canvas_visits")
          .select("id, visited_at, outcome, notes, voice_note_url, photo_storage_path, appointment_at, visitor:team_roster!canvas_visits_visited_by_fkey(full_name)")
          .eq("knock_pin_id", knockPinId)
          .order("visited_at", { ascending: false }),
        // Latest pending appt (if any)
        supabase
          .from("pending_canvas_appointments")
          .select("id, submitted_at, status, homeowner_first, homeowner_last, appointment_at, submitter:team_roster!pending_canvas_appointments_submitted_by_fkey(full_name)")
          .eq("knock_pin_id", knockPinId)
          .order("submitted_at", { ascending: false }),
      ]);
      if (cancelled) return;

      const out: Entry[] = [];

      if (visitsRes.data) {
        for (const v of visitsRes.data as any[]) {
          out.push({
            id: `visit-${v.id}`,
            kind: "visit",
            at: v.visited_at,
            actor_name: v.visitor?.full_name ?? null,
            outcome: v.outcome,
            notes: v.notes,
            voice_note_url: v.voice_note_url,
            photo_storage_path: v.photo_storage_path,
            appointment_at: v.appointment_at,
          });
        }
      }

      if (pendingRes.data) {
        for (const p of pendingRes.data as any[]) {
          out.push({
            id: `pending-${p.id}`,
            kind: "pending_appt",
            at: p.submitted_at,
            actor_name: p.submitter?.full_name ?? null,
            outcome: p.status === "pending" ? "pending Alex" : p.status,
            notes: `${p.homeowner_first ?? ""} ${p.homeowner_last ?? ""}`.trim() || null,
            voice_note_url: null,
            photo_storage_path: null,
            appointment_at: p.appointment_at,
          });
        }
      }

      if (pinRes.data) {
        const p: any = pinRes.data;
        out.push({
          id: `pin-${p.id}`,
          kind: "pin_dropped",
          at: p.dropped_at ?? new Date().toISOString(),
          actor_name: p.dropper?.full_name ?? null,
          outcome: null,
          notes: p.notes ?? null,
          voice_note_url: null,
          photo_storage_path: null,
          appointment_at: null,
        });
      }

      // Newest first
      out.sort((a, b) => b.at.localeCompare(a.at));
      setEntries(out);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [knockPinId]);

  if (!knockPinId) return null;
  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Knock & Contact History</div>
        <div className="mt-2 text-sm text-slate-500">Loading…</div>
      </div>
    );
  }
  if (entries.length === 0) return null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Knock & Contact History</div>
        <div className="text-[10px] text-slate-500">{entries.length} event{entries.length === 1 ? "" : "s"}</div>
      </div>
      <div className="divide-y divide-slate-100">
        {entries.map((e) => (
          <div key={e.id} className="px-4 py-2.5">
            <div className="flex flex-wrap items-baseline gap-x-2 text-xs">
              <span className="font-mono text-slate-400">
                {new Date(e.at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
              </span>
              <span className="text-slate-400">· {relativeTime(e.at)}</span>
              {e.actor_name ? <strong className="text-slate-700">{e.actor_name}</strong> : null}
              {e.kind === "pin_dropped" ? (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">📍 Pin dropped</span>
              ) : null}
              {e.kind === "pending_appt" ? (
                <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-800">App · {e.outcome}</span>
              ) : null}
              {e.kind === "visit" && e.outcome ? (
                <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold text-purple-800">
                  {OUTCOME_LABEL[e.outcome] ?? e.outcome}
                </span>
              ) : null}
              {e.appointment_at ? (
                <span className="text-slate-500">· appt {new Date(e.appointment_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}</span>
              ) : null}
            </div>
            {e.notes ? <div className="mt-1 text-sm text-slate-700">{e.notes}</div> : null}
            {(e.voice_note_url || e.photo_storage_path) ? (
              <VisitMediaInline voicePath={e.voice_note_url} photoPath={e.photo_storage_path} />
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
